import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { ApiError, api, type AuthUser } from "../lib/api";
import { useAuthStatus, useMe } from "../App";
import { AppFooter } from "../components/AppFooter";
import { AppHeader } from "../components/AppHeader";
import { Spinner } from "../components/ui";

export default function LoginPage() {
  const status = useAuthStatus();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => api.post<{ user: AuthUser }>("/api/auth/login", { email: identifier, password }),
    onSuccess: (res) => {
      queryClient.setQueryData(["me"], res.user);
      queryClient.invalidateQueries({ queryKey: ["auth-status"] });
      navigate("/", { replace: true });
    },
  });

  if (me.data) return <Navigate to="/" replace />;
  if (status.data?.needsSetup) return <Navigate to="/setup" replace />;

  const error = login.error instanceof ApiError ? login.error.message : null;

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader />
      <main className="flex flex-1 items-center justify-center px-4 py-6">
        <div className="card w-full max-w-sm p-6">
          <div className="mb-6 flex items-center gap-2">
            <span className="flex size-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <BarChart3 size={18} />
            </span>
            <div>
              <h1 className="text-base font-semibold text-slate-900">Statistik</h1>
              <p className="text-xs text-slate-500">Masuk ke dasbor analitik</p>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate();
            }}
          >
            <div>
              <label className="label" htmlFor="identifier">
                Email atau username
              </label>
              <input
                id="identifier"
                type="text"
                className="input"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button type="submit" className="btn-primary w-full" disabled={login.isPending}>
              {login.isPending && <Spinner className="text-white" />}
              Masuk
            </button>
          </form>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
